var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useEffect, useMemo, useState } from "react";
import { Routes, Route, useNavigate, Navigate, useMatch } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { Footer, Header, NotFoundScreen, PageBanner } from "@sequentech/ui-essentials";
import { applyConfigurationLanguagePolicy, ETranslationScope, overwriteTranslations, setCookie, USER_LANGUAGE_COOKIE_NAME, getValueFromCookie, } from "@sequentech/ui-core";
import { HomeScreen } from "./screens/HomeScreen";
import { ConfirmationScreen } from "./screens/ConfirmationScreen";
import Stack from "@mui/material/Stack";
import { provideBallotService } from "./services/BallotService";
import { AuthContext } from "./providers/AuthContextProvider";
import { RouteParameterProvider } from ".";
import { ApolloContextProvider, ApolloWrapper } from "./providers/ApolloContextProvider";
import { LoginScreen } from "./screens/LoginScreen";
import { SettingsContext } from "./providers/SettingsContextProvider";
import { useAppSelector } from "./store/hooks";
import { selectBallotStyleByElectionEventId } from "./store/ballotStyles/ballotStylesSlice";
import SequentLogo from "@sequentech/ui-essentials/public/Sequent_logo.svg";
import BlankLogoImg from "@sequentech/ui-essentials/public/blank_logo.svg";
var StyledAppWrapper = styled(Stack)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    ", "\n"], ["\n    ", "\n"])), function (_a) {
    var customCss = _a.customCss;
    return customCss;
});
var StyledApp = styled(Stack)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    min-height: 100vh;\n"], ["\n    min-height: 100vh;\n"])));
var HeaderWithContext = function () {
    var _a, _b, _c;
    var authContext = useContext(AuthContext);
    var globalSettings = useContext(SettingsContext).globalSettings;
    var routeMatch = useMatch("/tenant/:tenantId/event/:eventId/*");
    var ballotStyle = useAppSelector(selectBallotStyleByElectionEventId(routeMatch === null || routeMatch === void 0 ? void 0 : routeMatch.params.eventId));
    var presentation = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml.election_event_presentation;
    var languagesList = (_b = (_a = presentation === null || presentation === void 0 ? void 0 : presentation.language_conf) === null || _a === void 0 ? void 0 : _a.enabled_language_codes) !== null && _b !== void 0 ? _b : ["en"];
    var showUserProfile = (_c = presentation === null || presentation === void 0 ? void 0 : presentation.show_user_profile) !== null && _c !== void 0 ? _c : true;
    var logoImg = (presentation === null || presentation === void 0 ? void 0 : presentation.logo_url) === undefined
        ? BlankLogoImg
        : (presentation === null || presentation === void 0 ? void 0 : presentation.logo_url) === null
            ? SequentLogo
            : presentation === null || presentation === void 0 ? void 0 : presentation.logo_url;
    var onChangeLanguage = function (lang) {
        if (getValueFromCookie(USER_LANGUAGE_COOKIE_NAME) !== lang) {
            setCookie(USER_LANGUAGE_COOKIE_NAME, lang);
        }
    };
    return (_jsx(Header, { appVersion: { main: globalSettings.APP_VERSION }, appHash: { main: globalSettings.APP_HASH }, userProfile: {
            firstName: authContext.firstName,
            username: authContext.username,
            email: authContext.email,
            openLink: showUserProfile ? authContext.openProfileLink : undefined,
        }, logoutFn: authContext.isAuthenticated ? authContext.logout : undefined, languagesList: languagesList, logoUrl: logoImg, onChangeLanguage: onChangeLanguage }));
};
var App = function () {
    var _a;
    var navigate = useNavigate();
    var globalSettings = useContext(SettingsContext).globalSettings;
    var _b = useState(null), confirmationBallot = _b[0], setConfirmationBallot = _b[1];
    var _c = useState(""), ballotId = _c[0], setBallotId = _c[1];
    var _d = useState(""), fileName = _d[0], setFileName = _d[1];
    var ballotService = provideBallotService();
    var routeMatch = useMatch("/tenant/:tenantId/event/:eventId/*");
    var currentEventId = routeMatch === null || routeMatch === void 0 ? void 0 : routeMatch.params.eventId;
    var ballotStyle = useAppSelector(selectBallotStyleByElectionEventId(currentEventId));
    useEffect(function () {
        setConfirmationBallot(null);
        setBallotId("");
        setFileName("");
    }, [currentEventId]);
    useEffect(function () {
        if (globalSettings.DISABLE_AUTH) {
            navigate("/tenant/".concat(globalSettings.DEFAULT_TENANT_ID, "/event/").concat(globalSettings.DEFAULT_EVENT_ID, "/start"));
        }
    }, [navigate]);
    useEffect(function () {
        var _a;
        var presentation = (_a = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml) === null || _a === void 0 ? void 0 : _a.election_event_presentation;
        overwriteTranslations(presentation, {
            scope: ETranslationScope.BALLOT_VERIFIER,
            changeDefaultLanguage: false,
        });
        applyConfigurationLanguagePolicy(presentation);
        return function () {
            overwriteTranslations(undefined, {
                scope: ETranslationScope.BALLOT_VERIFIER,
                changeDefaultLanguage: false,
            });
        };
    }, [(_a = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml) === null || _a === void 0 ? void 0 : _a.election_event_presentation]);
    var customCss = useMemo(function () {
        var _a, _b, _c, _d, _e, _f;
        return (_f = (_c = (_b = (_a = confirmationBallot === null || confirmationBallot === void 0 ? void 0 : confirmationBallot.election_config) === null || _a === void 0 ? void 0 : _a.election_event_presentation) === null || _b === void 0 ? void 0 : _b.css) !== null && _c !== void 0 ? _c : (_e = (_d = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml) === null || _d === void 0 ? void 0 : _d.election_event_presentation) === null || _e === void 0 ? void 0 : _e.css) !== null && _f !== void 0 ? _f : "";
    }, [confirmationBallot, ballotStyle]);
    return (_jsx(StyledAppWrapper, { customCss: customCss, children: _jsxs(StyledApp, { className: "ballot-verifier app-root", children: [globalSettings.DISABLE_AUTH ? _jsx(Header, {}) : _jsx(HeaderWithContext, {}), _jsx(PageBanner, { marginBottom: "auto", children: _jsxs(Routes, { children: [_jsx(Route, { path: "*", element: _jsx(NotFoundScreen, {}) }), _jsx(Route, { path: "/", element: _jsx(Navigate, { replace: true, to: "/tenant/".concat(globalSettings.DEFAULT_TENANT_ID, "/event/").concat(globalSettings.DEFAULT_EVENT_ID, "/login") }) }), _jsx(Route, { path: "/tenant/:tenantId/event/:eventId/login", element: _jsx(RouteParameterProvider, { children: _jsx(ApolloContextProvider, { children: _jsx(ApolloWrapper, { children: _jsx(LoginScreen, {}) }) }) }) }), _jsx(Route, { path: "/tenant/:tenantId/event/:eventId/start", element: _jsx(RouteParameterProvider, { children: _jsx(ApolloContextProvider, { children: _jsx(ApolloWrapper, { children: _jsx(HomeScreen, { confirmationBallot: confirmationBallot, setConfirmationBallot: setConfirmationBallot, ballotId: ballotId, setBallotId: setBallotId, fileName: fileName, setFileName: setFileName, ballotService: ballotService }) }) }) }) }), _jsx(Route, { path: "/tenant/:tenantId/event/:eventId/confirmation", element: _jsx(RouteParameterProvider, { children: _jsx(ApolloContextProvider, { children: _jsx(ApolloWrapper, { children: _jsx(ConfirmationScreen, { confirmationBallot: confirmationBallot, ballotId: ballotId }) }) }) }) })] }) }), _jsx(Footer, {})] }) }));
};
export default App;
var templateObject_1, templateObject_2;
